import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ScrollText, ListChecks, CheckCircle2, AlertTriangle, XCircle, Sparkles, HelpCircle, SkipForward } from "lucide-react";
import { RecipeEditor } from "../components/RecipeEditor";
import { ResolveIngredient } from "../components/ResolveIngredient";

const money = (n, dp = 2) => (n == null ? "—" : `€ ${Number(n).toFixed(dp)}`);
const ANALYSIS_STATUS = {
  PRONTO: { label: "PRONTO", Icon: CheckCircle2, cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" },
  DA_CONFERMARE: { label: "DA CONFERMARE", Icon: AlertTriangle, cls: "bg-amber-500/15 text-amber-600" },
  MISSING: { label: "INGREDIENTI MANCANTI", Icon: XCircle, cls: "bg-rose-500/15 text-rose-600" },
  CUSTOM: { label: "CUSTOM", Icon: Sparkles, cls: "bg-sky-500/15 text-sky-600" },
  UNKNOWN: { label: "NON RICONOSCIUTO", Icon: HelpCircle, cls: "bg-muted text-muted-foreground" },
  GIA_PRESENTE: { label: "GIÀ PRESENTE", Icon: SkipForward, cls: "bg-slate-500/15 text-slate-600" },
};
const ING_DOT = { resolved: "text-[hsl(var(--success))]", selection_required: "text-amber-600", missing: "text-rose-600" };
const STATUS = {
  ok: { label: "OK", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" },
  costo_alto: { label: "Costo alto", cls: "bg-amber-500/15 text-amber-600" },
  incompleta: { label: "Incompleta", cls: "bg-rose-500/15 text-rose-600" },
};

export default function FoodCostRecipes() {
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisFilter, setAnalysisFilter] = useState("recognized");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);
  const [editorContext, setEditorContext] = useState({});

  const stdIngs = (r) => (r.ingredients || []).map((i) => ({ name: i.name, ingredient_id: i.ingredient_id, qty: i.qty, unit: i.unit }));
  const openEditorFromRow = async (r) => {
    if (r.status === "GIA_PRESENTE" && r.recipe_id) {
      try {
        const { data } = await api.get(`/fc/recipes/${r.recipe_id}`);
        setEditorInitial({ name: data.name, portions: data.portions, selling_price: data.selling_price, vat_rate: data.vat_rate,
          ingredients: (data.ingredients || []).map((i) => ({ name: i.name, ingredient_id: i.ingredient_id || "", quantity: i.quantity, unit: i.unit })),
          shelf_life_hours: data.shelf_life_hours ?? "", haccp_notes: data.haccp_notes || "",
          source_type: data.source_type || "manual", standard_recipe_id: data.standard_recipe_id || "", sheet_id: data.sheet_id || "" });
        setEditorContext({ mode: "edit", recipeId: r.recipe_id, standardIngredients: stdIngs(r) });
      } catch (e) { return toast.error(formatApiError(e.response?.data?.detail)); }
    } else if (r.match === "CUSTOM" || r.match === "UNKNOWN") {
      setEditorInitial({ name: r.product, selling_price: r.price, vat_rate: r.sales_vat_rate, ingredients: [], source_type: "custom", dish_id: r.dish_id });
      setEditorContext({ mode: "new" });
    } else {
      setEditorInitial({ name: r.product, portions: 1, selling_price: r.price, vat_rate: r.sales_vat_rate,
        ingredients: (r.ingredients || []).map((i) => ({ name: i.name, ingredient_id: i.ingredient_id, quantity: i.qty, unit: i.unit })),
        source_type: "standard_customized", standard_recipe_id: r.standard_recipe, standard_recipe_version: 1, dish_id: r.dish_id });
      setEditorContext({ mode: "new", standardIngredients: stdIngs(r) });
    }
    setEditorOpen(true);
  };
  const onEditorSaved = () => { setEditorOpen(false); load(); if (showAnalysis) analyze(); };

  const analyze = async () => {
    setAnalyzing(true); setShowAnalysis(true);
    try {
      const { data } = await api.get("/recipes/analyze-menu");
      setAnalysis(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setShowAnalysis(false); }
    finally { setAnalyzing(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/fc/recipes");
      setRows(r.data.items || []); setCfg(r.data.settings || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditorInitial({ name: "", portions: 1, selling_price: 0, vat_rate: 10, ingredients: [], source_type: "manual" });
    setEditorContext({ mode: "new" });
    setEditorOpen(true);
  };
  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/fc/recipes/${id}`);
      setEditorInitial({ name: data.name, portions: data.portions, selling_price: data.selling_price, vat_rate: data.vat_rate,
        ingredients: (data.ingredients || []).map((x) => ({ name: x.name, ingredient_id: x.ingredient_id || "", quantity: x.quantity, unit: x.unit || "" })),
        shelf_life_hours: data.shelf_life_hours ?? "", haccp_notes: data.haccp_notes || "",
        source_type: data.source_type || "manual", standard_recipe_id: data.standard_recipe_id || "", sheet_id: data.sheet_id || "" });
      setEditorContext({ mode: "edit", recipeId: id });
      setEditorOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare questa ricetta?")) return; try { await api.delete(`/food-costs/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div data-testid="fc-recipes-page">
      <PageHeader title="Food Cost · Ricette" subtitle="Costo ricetta calcolato in automatico dallo storico fatture" testid="fc-recipes-header" />
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" onClick={analyze} className="rounded-full" data-testid="fc-analyze-menu"><ListChecks className="w-4 h-4 mr-1" />Analizza menu</Button>
        <Button onClick={openNew} className="rounded-full" data-testid="fc-add-recipe"><Plus className="w-4 h-4 mr-1" />Nuova ricetta</Button>
      </div>

      <Card className="p-5 rounded-xl" data-testid="fc-recipes-card">
        {loading ? (
          <p className="text-muted-foreground py-8 text-center">Caricamento…</p>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground" data-testid="fc-recipes-empty">
            <ScrollText className="w-10 h-10 mx-auto mb-3 text-primary/60" />
            Nessuna ricetta. Crea la prima collegando gli ingredienti master.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-2">Prodotto</th><th className="py-2 px-2 text-right">Prezzo lordo</th>
                <th className="py-2 px-2 text-right">IVA</th><th className="py-2 px-2 text-right">Ricavo netto</th>
                <th className="py-2 px-2 text-right">Costo ricetta</th><th className="py-2 px-2 text-right">FC netto %</th>
                <th className="py-2 px-2 text-right">Margine netto €</th><th className="py-2 px-2 text-right">Marg. netto %</th>
                <th className="py-2 px-2 text-center">Stato</th><th className="py-2 px-2"></th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const st = STATUS[r.status] || STATUS.incompleta;
                  return (
                    <tr key={r.id} className="border-b border-border/60 last:border-0" data-testid={`fc-recipe-row-${r.id}`}>
                      <td className="py-2 px-2 font-medium">{r.name}</td>
                      <td className="py-2 px-2 text-right">{money(r.selling_price)}</td>
                      <td className="py-2 px-2 text-right text-muted-foreground">{r.vat_rate == null ? "—" : `${r.vat_rate}%`}</td>
                      <td className="py-2 px-2 text-right">{money(r.net_selling_price)}</td>
                      <td className="py-2 px-2 text-right">{money(r.cost_per_portion, 4)}</td>
                      <td className="py-2 px-2 text-right font-semibold" data-testid={`fc-recipe-fcnet-${r.id}`}>{r.food_cost_pct_net == null ? "—" : `${r.food_cost_pct_net}%`}</td>
                      <td className="py-2 px-2 text-right">{r.margin_net == null ? "—" : money(r.margin_net)}</td>
                      <td className="py-2 px-2 text-right text-muted-foreground">{r.margin_pct_net == null ? "—" : `${r.margin_pct_net}%`}</td>
                      <td className="py-2 px-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`fc-recipe-status-${r.id}`}>{st.label}</span></td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r.id)} data-testid={`fc-edit-recipe-${r.id}`}><Pencil className="w-4 h-4 text-primary" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(r.id)} data-testid={`fc-del-recipe-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={showAnalysis} onOpenChange={setShowAnalysis}>
        <DialogContent className="max-w-6xl" data-testid="fc-analysis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-primary" />Analisi menu · ricette standard <span className="text-xs font-normal text-muted-foreground">(sola lettura — non crea né modifica ricette)</span></DialogTitle>
          </DialogHeader>
          {analyzing ? (
            <p className="text-muted-foreground py-10 text-center" data-testid="fc-analysis-loading">Analisi in corso…</p>
          ) : !analysis ? null : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs" data-testid="fc-analysis-counts">
                <span className="px-2.5 py-1 rounded-full bg-muted">Totale {analysis.counts.total}</span>
                <span className="px-2.5 py-1 rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">Pronte {analysis.counts.ready}</span>
                <span className="px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600">Da confermare {analysis.counts.to_confirm}</span>
                <span className="px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-600">Mancanti {analysis.counts.missing}</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-500/15 text-slate-600">Già presenti {analysis.counts.already_present}</span>
                <span className="px-2.5 py-1 rounded-full bg-sky-500/15 text-sky-600">Custom {analysis.counts.CUSTOM}</span>
                <span className="px-2.5 py-1 rounded-full bg-muted text-muted-foreground">Non riconosciuti {analysis.counts.UNKNOWN}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant={analysisFilter === "recognized" ? "default" : "outline"} className="rounded-full h-8" onClick={() => setAnalysisFilter("recognized")} data-testid="fc-analysis-filter-std">Ricette standard ({analysis.recognized.length})</Button>
                <Button size="sm" variant={analysisFilter === "all" ? "default" : "outline"} className="rounded-full h-8" onClick={() => setAnalysisFilter("all")} data-testid="fc-analysis-filter-all">Tutti i prodotti ({analysis.rows.length})</Button>
              </div>
              <div className="overflow-auto max-h-[60vh] border border-border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card"><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                    <th className="py-2 px-2">Prodotto</th><th className="py-2 px-2">Ricetta std</th>
                    <th className="py-2 px-2">Ingredienti &amp; dosi</th>
                    <th className="py-2 px-2 text-right">Prezzo</th><th className="py-2 px-2 text-right">Costo</th>
                    <th className="py-2 px-2 text-right">FC netto</th><th className="py-2 px-2 text-right">Margine</th>
                    <th className="py-2 px-2">Stato</th><th className="py-2 px-2 text-right">Azioni</th>
                  </tr></thead>
                  <tbody>
                    {(analysisFilter === "recognized" ? analysis.recognized : analysis.rows).map((r) => {
                      const st = ANALYSIS_STATUS[r.status] || ANALYSIS_STATUS.UNKNOWN;
                      return (
                        <tr key={r.dish_id} className="border-b border-border/60 last:border-0 align-top" data-testid={`fc-analysis-row-${r.dish_id}`}>
                          <td className="py-2 px-2 font-medium">{r.product}</td>
                          <td className="py-2 px-2 text-muted-foreground">{r.standard_recipe || "—"}</td>
                          <td className="py-2 px-2">
                            {r.ingredients?.length ? (
                              <ul className="space-y-0.5">
                                {r.ingredients.map((ing, i) => (
                                  <li key={i} className="flex items-start gap-1.5 text-xs">
                                    <span className={`mt-0.5 ${ING_DOT[ing.status] || "text-muted-foreground"}`}>●</span>
                                    <span><b>{ing.name}</b> {ing.qty}{ing.unit}
                                      {ing.status === "resolved" && <span className="text-[hsl(var(--success))]"> → {ing.master_name}{ing.confirmed ? " ✔" : ""}{ing.cost != null ? ` (€${ing.cost.toFixed(4)})` : ""}</span>}
                                      {ing.status === "selection_required" && <span className="text-amber-600"> → scegli: {ing.candidates.join(" / ")}</span>}
                                      {ing.status === "missing" && <span className="text-rose-600"> → MANCANTE</span>}
                                    </span>
                                    {ing.status !== "resolved" && <ResolveIngredient standardRecipe={r.standard_recipe} ingredientName={ing.name} onResolved={analyze} />}
                                  </li>
                                ))}
                              </ul>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">{money(r.price)}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">{r.cost_known ? money(r.total_cost) : "—"}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap font-semibold">{r.food_cost_net_pct == null ? "—" : `${r.food_cost_net_pct}%`}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">{r.margin_net == null ? "—" : money(r.margin_net)}</td>
                          <td className="py-2 px-2"><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${st.cls}`} data-testid={`fc-analysis-status-${r.dish_id}`}><st.Icon className="w-3.5 h-3.5" />{st.label}</span></td>
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            {r.status === "GIA_PRESENTE" && <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => openEditorFromRow(r)} data-testid={`fc-edit-${r.dish_id}`}><Pencil className="w-3 h-3 mr-1" />Modifica</Button>}
                            {(r.match === "STANDARD_EXACT" || r.match === "STANDARD_PROBABLE") && r.status !== "GIA_PRESENTE" && <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => openEditorFromRow(r)} data-testid={`fc-build-${r.dish_id}`}><Pencil className="w-3 h-3 mr-1" />Crea/Modifica</Button>}
                            {r.match === "CUSTOM" && <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => openEditorFromRow(r)} data-testid={`fc-create-${r.dish_id}`}><Plus className="w-3 h-3 mr-1" />Crea ricetta</Button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">L'analisi è di sola lettura. Usa <b>Risolvi</b> sugli ingredienti o <b>Crea/Modifica</b> per agire: ogni scrittura è un'azione esplicita. Modificando una ricetta standard viene creata la <b>Ricetta Operativa Dogana</b>; il catalogo standard resta invariato.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editorOpen && <RecipeEditor open={editorOpen} onOpenChange={setEditorOpen} initial={editorInitial} context={editorContext} onSaved={onEditorSaved} />}
    </div>
  );
}
